import {
  EmailInvitation,
  EmailInvitationParams,
  Enterprise,
  EnterpriseParams,
  LoginResponse,
  RegisterParams,
  Tag,
  User,
  UserParams,
} from '../modules/auth';
import { Project, ProjectParams, SharedFile } from '../modules/projects/types';
import client from './client';
import {
  Contract,
  ContractParams,
  ContractSOV,
  ContractType,
  EmptyContractSOVParams,
} from '../modules/contracts';
import {
  ChangeOrder,
  ChangeOrderParams,
  ChangeOrderSOV,
} from '../modules/change-orders';
import { getContractSubUrl } from '../util/utils';
import { BillingPeriod, BillingPeriodParams } from '../modules/billing-periods';
import { PartnerParams } from '../modules/partners';
import {
  BidPackage,
  BidPackageLineItem,
  BidPackageParams,
} from '../modules/bid-packages';
import { Bidder, BidderParams } from '../modules/bidders';
import {
  BidChance,
  BidChanceParams,
  BidProposal,
  BidProposalParams,
} from '../modules/bid-chances';
import streamSaver from 'streamsaver';
import qs from 'qs';
import { Vendor, VendorEvaluation, VendorParams } from '../modules/vendors';
import { Expense, ExpenseParams } from '../modules/expenses';
import { Invoice, InvoiceParams, TaxInvoice } from '../modules/invoices';
import {
  Schedule,
  ScheduleListType,
  ScheduleParams,
} from '../modules/schedules/types';
import { WorkRecord, WorkRecordParams } from '../modules/work-records';
import { Task, TaskParams } from '../modules/tasks';

export async function postPartnerRegistration(params: EmailInvitationParams) {
  const response = await client.post<EmailInvitation>(
    `/enterprises/${params.enterpriseId}/partner-registration`,
    {
      status: params.status,
    },
  );
  return response.data;
}

export async function getPartnerRegistration(params: EmailInvitationParams) {
  const response = await client.get<EmailInvitation>(
    `/enterprises/${params.enterpriseId}/partner-registration`,
  );
  return response.data;
}

export async function searchEnterprise(name: string) {
  const response = await client.get<Enterprise[]>(
    `/auth/search-enterprise?name=${name}`,
  );
  return response.data;
}

export async function getEnterpriseTags(type: string) {
  const response = await client.get<Tag[]>(
    `/auth/get-enterprise-tags?type=${type}`,
  );
  return response.data;
}

export async function sendEmailInvitation(params: EmailInvitationParams) {
  const response = await client.post<EmailInvitation[]>(
    `/enterprises/${params.enterpriseId}/email-invitations/send`,
    { idList: params.idList },
  );
  return response.data;
}

export async function getEmailInvitationList(params: EmailInvitationParams) {
  const response = await client.get<EmailInvitation[]>(
    `/enterprises/${params.enterpriseId}/email-invitations?type=${params.type}`,
  );
  return response.data;
}

export async function bulkCreateEmailInvitation(params: EmailInvitationParams) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/email-invitations/bulk-create`,
    params.dto,
  );
  return response.data;
}

export async function bulkDeleteEmailInvitation(params: EmailInvitationParams) {
  const response = await client.post<EmailInvitation[]>(
    `/enterprises/${params.enterpriseId}/email-invitations/bulk-delete`,
    { idList: params.idList },
  );
  return response.data;
}

export async function registerEnterprise(enterpriseId: number) {
  const response = await client.patch<User>(`/auth/enterprises`, {
    enterpriseId,
  });
  return response.data;
}

export async function createEnterprise(enterprise: Enterprise) {
  const response = await client.post<Enterprise>(`/enterprises`, enterprise);
  return response.data;
}

export async function updateEnterprise(params: EnterpriseParams) {
  const response = await client.patch<Enterprise>(
    `/enterprises/${params.enterpriseId}`,
    params.enterprise,
  );
  return response.data;
}

export async function getMyEnterprise(enterpriseId: string) {
  const response = await client.get<Enterprise>(`/enterprises/${enterpriseId}`);
  return response.data;
}

export async function getLoggedInEnterprise(enterpriseId: string) {
  const response = await client.get<Enterprise>(
    `auth/enterprises/${enterpriseId}`,
  );
  return response.data;
}

export async function getProjectList(params: ProjectParams) {
  let url = `/enterprises/${params.enterpriseId}/projects`;
  let queryString = '';
  if ('filter' in params) {
    queryString = qs.stringify(params.filter);
  }

  if (queryString) url += '?' + queryString;

  const response = await client.get<{ projects: Project[] }>(url);
  return response.data.projects;
}

export async function getProjectDetail(params: ProjectParams) {
  const response = await client.get<Project>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}`,
  );
  return response.data;
}

export async function getProjectSimpleData(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/simple-data?approvalType=${params.approvalType}`,
  );
  return response.data;
}

export async function getProjectListSimple(params: any) {
  const response = await client.get<any[]>(
    `/enterprises/${params.enterpriseId}/project-list-simple`,
  );
  return response.data;
}

export async function updateProject(params: ProjectParams) {
  const response = await client.patch<Project>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}`,
    params.project,
  );
  return response.data;
}

export async function createProject(params: ProjectParams) {
  const response = await client.post<Project>(
    `/enterprises/${params.enterpriseId}/projects`,
    params.project,
  );
  return response.data;
}

export async function deleteProject(params: ProjectParams) {
  const response = await client.delete<Project>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}`,
  );
  return response.data;
}

// export async function updateProjectConfig(params: ProjectParams) {
//   const response = await client.patch<any>(
//     `/enterprises/${params.enterpriseId}/projects/${params.projectId}/project-config/${params.projectConfigId}`,
//     params.projectConfig,
//   );
//   return response.data;
// }

// export async function getProjectConfig(params: ProjectParams) {
//   const response = await client.get<any>(
//     `/enterprises/${params.enterpriseId}/projects/${params.projectId}/project-config/${params.projectConfigId}`,
//     params.projectConfig,
//   );
//   return response.data;
// }

export async function getProjectSharedFiles(params: ProjectParams) {
  const response = await client.get<SharedFile[]>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/shared-files`,
  );
  return response.data;
}

export async function uploadProjectSharedFiles(params: ProjectParams) {
  const response = await client.post<SharedFile[]>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/shared-files`,
    { file: params.file },
  );
  return response.data;
}

export async function removeProjectSharedFile(params: ProjectParams) {
  const response = await client.delete<SharedFile[]>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/shared-files/${params.sharedFileId}`,
  );
  return response.data;
}

export async function getPartnerList(params: PartnerParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params?.projectId) url += `/projects/${params?.projectId}/partners`;
  else url += `/partners`;
  const response = await client.get<{ enterprises: Enterprise[] }>(url);
  return response.data;
}

export async function getPartnerDetail(params: PartnerParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params?.projectId)
    url += `/projects/${params?.projectId}/partners/${params?.partnerId}`;
  else url += `/partners/${params?.partnerId}`;
  const response = await client.get<{ enterprises: Enterprise[] }>(url);
  return response.data;
}

export async function deletePartner(params: PartnerParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params?.projectId)
    url += `/projects/${params?.projectId}/partners/${params?.partnerId}`;
  else url += `/partners/${params?.partnerId}`;
  const response = await client.delete<{ id: number }>(url);
  return response.data;
}

export async function getParticipants(
  enterpriseId: string,
  projectId?: string,
) {
  let url = `/enterprises/${enterpriseId}`;
  if (projectId) url = url + `/projects/${projectId}/users`;
  else url = url + '/collaborators';

  const response = await client.get<User[]>(url);
  return response.data;
}

export async function getActiveEnterpriseUserList(enterpriseId: string) {
  let url = `/enterprises/${enterpriseId}/active-users`;
  const response = await client.get<User[]>(url);
  return response.data;
}

export async function getAllCollaborators(enterpriseId: string, data: any) {
  let url = `/enterprises/${enterpriseId}/vendors`;
  const queryString = qs.stringify(data);
  if (queryString) url += '?' + queryString;
  const response = await client.get<any[]>(url);
  return response.data;
}

export async function searchProjectUsers(
  enterpriseId: string,
  projectId?: string,
  partnerId?: string,
) {
  let url = `/enterprises/${enterpriseId}`;
  if (projectId) url = url + `/projects/${projectId}`;
  url = url + '/search-users';
  if (partnerId) url = url + `?partnerId=${partnerId}`;
  const response = await client.get<User[]>(url);
  return response.data;
}

export async function removeCollaborator(
  enterpriseId: string,
  projectId: string,
  userId: string,
) {
  const url = `/enterprises/${enterpriseId}/projects/${projectId}/users/${userId}`;
  const response = await client.delete<null>(url);
  return response.data;
}

export async function addUserToProject(
  enterpriseId: string,
  projectId: string,
  userIdList: number[],
) {
  let url = `/enterprises/${enterpriseId}/projects/${projectId}/add-users`;
  const response = await client.post<string>(url, { userIdList });
  return response.data;
}

export async function getMyEnterpriseList() {
  const response = await client.get<{ enterprises: Enterprise[] }>(
    '/auth/enterprises',
  );
  return response.data;
}

export async function searchEnterprisePartnersByName(
  enterpriseId: string,
  projectId: string,
  name: string,
) {
  const response = await client.get<any[]>(
    `/enterprises/${enterpriseId}/search-partners?name=${name}`,
  );
  return response.data;
}

export async function getMyMemberListByName(
  enterpriseId: string,
  name: string,
) {
  const response = await client.get<any[]>(
    `/enterprises/${enterpriseId}/search-username?name=${name}`,
  );
  return response.data;
}

export async function getContractList(params: ContractParams) {
  const response = await client.get<{ contracts: Contract[] }>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}`,
  );
  return response.data.contracts;
}

export async function getContractList2(params: ContractParams) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/list2`,
  );
  if (response.data === '') return null;
  else return response.data;
}

export async function createContract(params: ContractParams) {
  const response = await client.post<Contract>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}`,
    params.contract,
  );
  return response.data;
}

export async function updateContract(params: ContractParams) {
  const response = await client.patch<Contract>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${params.contract?.id}`,
    params.contract,
  );
  return response.data;
}

export async function updateContractMetaData(params: ContractParams) {
  const response = await client.patch<Contract>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/meta-data`,
    params.metaData,
  );
  return response.data;
}

export async function deleteContract(params: ContractParams) {
  const response = await client.delete<number>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${params.contract?.id}`,
  );
  return response.data;
}

export async function getContractDetail(params: ContractParams) {
  const response = await client.get<{ contract: Contract }>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${params.contractId}`,
  );
  return response.data;
}

export async function createContractSOV(params: ContractParams) {
  const response = await client.post<ContractSOV>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/schedule-of-values`,
    params.contractSOV,
  );
  return response.data;
}

export async function updateContractSOV(params: ContractParams) {
  const response = await client.patch<ContractSOV>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/schedule-of-values/${params.contractSOV?.id}`,
    params.contractSOV,
  );
  return response.data;
}

export async function deleteContractSOV(params: ContractParams) {
  const response = await client.delete<number>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/schedule-of-values/${params.contractSOV?.id}`,
  );
  return response.data;
}

export async function deleteAttachFile(apiUrl: string) {
  const response = await client.delete<any>(apiUrl);
  return response.data;
}

export async function downloadAttachFile(
  enterpriseId: string,
  projectId: string,
  contractType1: ContractType,
  filePath: string,
) {
  let apiUrl: any = `/enterprises/${enterpriseId}/projects/${projectId}/${getContractSubUrl(
    contractType1,
  )}/attach-file/download?path=${filePath}`;
  const response = await client.get<any>(apiUrl, { responseType: 'blob' });
  return response.data;
}

/*
export async function downloadFile(subUrl: string, attachFileId: string) {
  let apiUrl: string = `/${subUrl}/${attachFileId}/download`;
  const response = await client.get<any>(apiUrl, { responseType: 'blob' });
  return response.data;
}
*/

export async function downloadFile(
  subUrl: string,
  attachFileId: string,
  fileName: string,
) {
  let apiUrl: string = `${process.env.REACT_APP_API_ENDPOINT}/${subUrl}/${attachFileId}/download`;
  const fileStream = await streamSaver.createWriteStream(fileName);
  const accessToken = await localStorage.getItem(
    process.env.REACT_APP_AUTH_TOKEN_NAME!,
  );
  const options = {
    method: 'GET',
    headers: {
      authorization: `Bearer ${accessToken}`,
    },
  };

  fetch(apiUrl, options).then((res: any) => {
    const readableStream: any = res.body;
    const writer = fileStream.getWriter();
    const reader = readableStream.getReader();
    const pump = () =>
      reader
        .read()
        .then((res: any) =>
          res.done ? writer.close() : writer.write(res.value).then(pump),
        );
    pump();
  });
}

export async function getChangeOrderList({
  enterpriseId,
  projectId,
  contractId,
  contractType1,
}: ChangeOrderParams) {
  const response = await client.get<{ changeOrders: ChangeOrder[] }>(
    `/enterprises/${enterpriseId}/projects/${projectId}/${getContractSubUrl(
      contractType1,
    )}/${contractId}/change-orders`,
  );
  return response.data.changeOrders;
}

export async function createChangeOrder(params: ChangeOrderParams) {
  const response = await client.post<ChangeOrder>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/change-orders`,
    params.changeOrder,
  );
  return response.data;
}

export async function updateChangeOrder(params: ChangeOrderParams) {
  const response = await client.patch<ChangeOrder>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/change-orders/${params.changeOrderId}`,
    params.changeOrder,
  );
  return response.data;
}

export async function deleteChangeOrder(params: ChangeOrderParams) {
  const response = await client.delete<number>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/change-orders/${params.changeOrder?.id}`,
  );
  return response.data;
}

export async function removeAllChangeOrderSOV(params: ChangeOrderParams) {
  const response = await client.delete<number>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/change-orders/${params.changeOrderId}/remove-all-sovs`,
  );
  return response.data;
}

export async function bulkCreateChangeOrderSOVs(params: ChangeOrderParams) {
  const response = await client.post<ChangeOrder>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/change-orders/${params.changeOrderId}/bulk-create-sovs`,
    params.uploadSOVs,
  );
  return response.data;
}

export async function getBillingPeriodList(params: BillingPeriodParams) {
  const response = await client.get<{ billingPeriods: BillingPeriod[] }>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/billing-periods`,
  );
  return response.data.billingPeriods;
}

export async function createBillingPeriod(params: BillingPeriodParams) {
  const response = await client.post<BillingPeriod>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/billing-periods`,
    params.billingPeriod,
  );
  return response.data;
}

export async function updateBillingPeriod(params: BillingPeriodParams) {
  const response = await client.patch<BillingPeriod>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/billing-periods/${params.billingPeriod?.id}`,
    params.billingPeriod,
  );
  return response.data;
}

export async function deleteBillingPeriod(params: BillingPeriodParams) {
  const response = await client.delete<number>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/billing-periods/${params.billingPeriod?.id}`,
  );
  return response.data;
}

export async function searchBillingPeriods(
  enterpriseId: string,
  projectId: string,
  label?: string,
) {
  let url = `/enterprises/${enterpriseId}/projects/${projectId}/billing-periods/search`;
  if (label) url = url + `?label=${label}`;
  const response = await client.get<any[]>(url);
  return response.data;
}

export async function searchInvoiceByName(inputObj: any, name: string) {
  const response = await client.get<any[]>(
    `/enterprises/${inputObj.enterpriseId}/projects/${
      inputObj.projectId
    }/${getContractSubUrl(inputObj.contractType1)}/${
      inputObj.contractId
    }/invoices/search?name=${name}`,
  );
  return response.data;
}

export async function getPrimeContractSOV(
  enterpriseId: string,
  projectId: string,
) {
  const response = await client.get<any[]>(
    `/enterprises/${enterpriseId}/projects/${projectId}/get-prime-contract-sov`,
  );
  return response.data;
}

export async function getAllSubContractSOV(
  enterpriseId: string,
  projectId: string,
) {
  const response = await client.get<any[]>(
    `/enterprises/${enterpriseId}/projects/${projectId}/get-all-subcontract-sov`,
  );
  return response.data;
}

export async function bulkCreateContractSOV(params: ContractParams) {
  const response = await client.post<Contract>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/schedule-of-values/bulk-create`,
    params.uploadedSOV,
  );
  return response.data;
}

export async function removeAllContractSOV(params: ContractParams) {
  const response = await client.patch<Contract>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/schedule-of-values-remove-all`,
    {},
  );
  return response.data;
}

export async function createEmptyContractSOV(params: EmptyContractSOVParams) {
  const response = await client.post<any[]>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/sub-contracts/${params.contractId}/schedule-of-values/create-empty-contract-sov`,
    { codes: params.codes },
  );
  return response.data;
}

export async function uploadProfileSignature(
  enterpriseId: string,
  sigData: any,
) {
  client.defaults.headers.common['Content-Type'] = 'multipart/form-data';

  let formData = new FormData();
  formData.append('file', sigData);
  formData.append('name', 'aaaa');
  const response = await client.post<any>(
    `/enterprises/${enterpriseId}/auth/profile/upload-signature`,
    formData,
  );
  return response.data;
}

export async function updateUser(params: UserParams) {
  const response = await client.patch<User>(`/auth/user-profile`, params.user);
  return response.data;
}

export async function updateUserRole(params: UserParams) {
  const response = await client.patch<User>(
    `/enterprises/${params.enterpriseId}/users/${params.userId}/roles`,
    params.user,
  );
  return response.data;
}

export async function getEnterpriseMemberList(params: UserParams) {
  const response = await client.get<User[]>(
    `/enterprises/${params.enterpriseId}/users`,
  );
  return response.data;
}

export async function getEnterpriseMemberList2(params: UserParams) {
  const response = await client.get<User[]>(
    `/enterprises/${params.enterpriseId}/active-users-2`,
  );
  return response.data;
}

export async function registerUser(params: RegisterParams) {
  const url = '/auth/register-1';
  const response = await client.post<LoginResponse>(url, params);
  return response.data;
}

export async function registerUserAndEnterprise(params: RegisterParams) {
  const url = '/auth/register-2';
  const response = await client.post<LoginResponse>(url, params);
  return response.data;
}

export async function loginUser(data: any) {
  const response = await client.post<LoginResponse>(`/auth/login`, data);
  return response.data;
}

export async function getLoggedInUser() {
  const response = await client.get<User>(`/auth/logged-in-user`);
  return response.data;
}

export async function getRoleForUser(params: UserParams) {
  const response = await client.get<string>(
    `/auth/enterprises/${params.enterpriseId}/users/${params.userId}/role`,
  );
  return response.data;
}

export async function getChangeOrderDetail(params: ChangeOrderParams) {
  const response = await client.get<{ changeOrder: ChangeOrder }>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/change-orders/${params.changeOrderId}`,
  );
  return response.data;
}

export async function createChangeOrderSOV(params: ChangeOrderParams) {
  const response = await client.post<ChangeOrderSOV>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/change-orders/${params.changeOrder?.id}/schedule-of-values`,
    params.changeOrderSOV,
  );
  return response.data;
}

export async function updateChangeOrderSOV(params: ChangeOrderParams) {
  const response = await client.patch<ChangeOrderSOV>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/change-orders/${params.changeOrder?.id}/schedule-of-values/${
      params.changeOrderSOV?.id
    }`,
    params.changeOrderSOV,
  );
  return response.data;
}

export async function deleteChangeOrderSOV(params: ChangeOrderParams) {
  const response = await client.delete<number>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/change-orders/${params.changeOrder?.id}/schedule-of-values/${
      params.changeOrderSOV?.id
    }`,
  );
  return response.data;
}

export async function searchContractSOV(
  enterpriseId: string,
  projectId: string,
  contractId: string,
  contractType1: ContractType,
  name: string,
) {
  const response = await client.get<any[]>(
    `/enterprises/${enterpriseId}/projects/${projectId}/${getContractSubUrl(
      contractType1,
    )}/${contractId}/schedule-of-values/search?name=${name}`,
  );
  return response.data;
}

export async function getBidPackageList(params: BidPackageParams) {
  const response = await client.get<{ bidPackages: BidPackage[] }>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages?isBudgeting=${params.isBudgeting}`,
  );
  return response.data;
}

export async function createBidPackage(params: BidPackageParams) {
  const response = await client.post<BidPackage>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages`,
    params.bidPackage,
  );
  return response.data;
}

export async function updateBidPackage(params: BidPackageParams) {
  const response = await client.patch<BidPackage>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackage?.id}`,
    params.bidPackage,
  );
  return response.data;
}

export async function deleteBidPackage(params: BidPackageParams) {
  const response = await client.delete<number>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackage?.id}`,
  );
  return response.data;
}

export async function getBidPackageDetail(params: BidPackageParams) {
  const response = await client.get<BidPackage>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}`,
  );
  return response.data;
}

export async function checkBriefingAttendants(params: BidPackageParams) {
  const response = await client.patch<any>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/briefing`,
    params.data,
  );
  return response.data;
}

export async function createEmptyBidPackageLineItem(params: BidPackageParams) {
  const response = await client.post<BidPackageLineItem[]>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/line-items`,
    { codes: params.codes },
  );
  return response.data;
}

export async function updateEmptyBidPackageLineItem(params: BidPackageParams) {
  const response = await client.patch<BidPackageLineItem[]>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/line-items`,
    params.items,
  );
  return response.data;
}

export async function deleteEmptyBidPackageLineItem(params: BidPackageParams) {
  const response = await client.delete<BidPackage>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/line-items`,
  );
  return response.data;
}

export async function getBidderList(params: BidderParams) {
  const response = await client.get<{ bidders: Bidder[] }>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/bidders`,
  );
  return response.data;
}

export async function getBidderListSimple(params: BidderParams) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/bidder-list-simple`,
  );
  return response.data;
}

export async function createBidders(params: BidderParams) {
  const response = await client.post<Bidder>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/bidders`,
    { vendorIdList: params.vendorIdList },
  );
  return response.data;
}

export async function createTempBidders(params: BidderParams) {
  const response = await client.post<Bidder>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/tempBidders`,
    params.tempBidders,
  );
  return response.data;
}

export async function updateBidder(params: BidderParams) {
  const response = await client.patch<Bidder>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/bidders/${params.bidder?.id}`,
    params.bidder,
  );
  return response.data;
}

export async function deleteBidder(params: BidderParams) {
  const response = await client.delete<number>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/bidders/${params.bidderId}`,
  );
  return response.data;
}

export async function getBidderDetail(params: BidderParams) {
  const response = await client.get<Bidder>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/bidders/${params.bidderId}`,
  );
  return response.data;
}

export async function createBehalfBidProposal(params: any) {
  const response = await client.post(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/bidders/${params.bidderId}/proposals`,
    params.bidProposal,
  );
  return response.data;
}

export async function copyWinningBidToContract(params: BidderParams) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/bidders/${params.bidderId}/copy-to-contract`,
    {
      createContractDto: params.createContractDto,
      lastBidProposalId: params.lastBidProposalId,
    },
  );
  return response.data;
}

export async function sendBidInvitation(params: BidderParams) {
  const response = await client.post<any[]>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/send-bid-invitations`,
    { bidderIdList: params.bidderIdList },
  );
  return response.data;
}

export async function requestProposalAgain(params: any) {
  const response = await client.patch<null>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/bidders/${params.bidderId}/reconfirm`,
    { reason: params.reason },
  );
  return response.data;
}

export async function bulkDeleteBidders(params: BidderParams) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/bidders/bulk-delete`,
    { bidderIdList: params.bidderIdList },
  );
  return response.data;
}

export async function getBidChanceList(params: BidChanceParams) {
  const response = await client.get<{ bidChances: BidChance[] }>(
    `/enterprises/${params.enterpriseId}/bid-chances`,
  );
  return response.data;
}

export async function getBidChanceDetail(params: BidChanceParams) {
  const response = await client.get<{ bidChance: BidChance }>(
    `/bid-chances/${params.bidChanceId}?token=${params.d1}`,
  );
  return response.data;
}

export async function getBidChanceDetail2(params: BidChanceParams) {
  const response = await client.get<{ bidChance: BidChance }>(
    `/wh/bid-invitation?d1=${params.d1}&d2=${params.d2}&input=${params.input}&bmt=${params.bmt}`,
  );
  return response.data;
}

export async function updateBidChance(params: BidChanceParams) {
  const response = await client.patch<BidChance>(
    `/bid-chances/${params.bidChance?.id}`,
    { status: params.bidChance?.status },
  );
  return response.data;
}

export async function updateBidChanceBidder(params: BidChanceParams) {
  const response = await client.patch<any>(
    `/enterprises/${params.enterpriseId}/bid-chances/${params.bidChanceId}/update-bidder`,
    {},
  );
  return response.data;
}

export async function getBidPackageLineItems(params: BidChanceParams) {
  const response = await client.get<BidPackageLineItem[]>(
    `/enterprises/${params.enterpriseId}/bid-chances/${params.bidChanceId}/bid-package-line-items?bidPackageId=${params.bidPackageId}`,
  );
  return response.data;
}

export async function createBidProposal(params: BidProposalParams) {
  const response = await client.post<{ bidChance: BidChance }>(
    `/bid-chances/${params.bidChanceId}/bid-proposals`,
    params.bidProposal,
  );
  return response.data;
}

export async function getBidProposalDetail(params: BidChanceParams) {
  const response = await client.get<BidProposal>(
    `/enterprises/${params.enterpriseId}/bid-chances/${params.bidChanceId}/bid-proposals/${params.bidProposalId}`,
  );
  return response.data;
}

export async function getVendorListByName(name: string, enterpriseId: string) {
  const response = await client.get<any[]>(
    `/enterprises/${enterpriseId}/vendors?name=${name}`,
  );
  return response.data;
}

export async function requestContractSign(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/electronic-sign/request-sign`,
    params.data,
  );
  return response.data;
}

export async function previewContractSignDoc(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/electronic-sign/preview-contract-sign-doc`,
    params.data,
  );
  return response.data;
}

export async function getSignTemplateList() {
  const response = await client.get<any>(`/auth/modusign/templates`);
  return response.data;
}

export async function getSignDocumentUrl(documentId: string) {
  const response = await client.get<any>(
    `/auth/sign-document-url?documentId=${documentId}`,
  );
  return response.data;
}

export async function getModusignTemplateUrl(templateId: string) {
  const response = await client.get<any>(
    `/modusign/template-url/${templateId}`,
  );
  return response.data;
}

export async function deleteModusignTemplat(templateId: string) {
  const response = await client.delete<any>(
    `/modusign/templates/${templateId}`,
  );
  return response.data;
}

export async function submitRequiredDocumentsForContractSign(params: any) {
  const response = await client.patch<any>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/electronic-sign/submit-docs`,
    params.data,
  );
  return response.data;
}

export async function purchaseRevenueStamp(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/electronic-sign/purchase-r-stamp`,
    {},
  );
  return response.data;
}

export async function checkPaymentRevenueStamp(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/electronic-sign/check-payment-r-stamp`,
    {},
  );
  return response.data;
}

export async function requestForSubmitSignDocs(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/projects/${
      params.projectId
    }/${getContractSubUrl(params.contractType1)}/${
      params.contractId
    }/electronic-sign/request-submit-docs`,
    params.data,
  );
  return response.data;
}

export async function connectToModusign(params: any) {
  const response = await client.post<any>(
    '/auth/modusign/connect',
    params.data,
  );
  return response.data;
}

export async function disconnectToModusign(params: any) {
  const response = await client.post<any>(
    '/auth/modusign/disconnect',
    params.data,
  );
  return response.data;
}

export async function registerUserToModusign(params: any) {
  const response = await client.post<any>(
    `/auth/modusign/register-user?state=${process.env.REACT_APP_MODU_SIGN_STATE}`,
    params.data,
  );
  return response.data;
}

export async function importModusignTemplates(params: any) {
  const response = await client.post<any>(`/modusign/import-templates`, {});
  return response.data;
}

export async function sendEmailResetPassword(params: any) {
  const response = await client.post<any>(`/auth/send-email-forgot-password`, {
    email: params.email,
  });
  return response.data;
}

export async function isResetPasswordTokenValid(params: any) {
  const response = await client.post<any>(
    `/auth/is-reset-password-token-valid`,
    {
      newPasswordToken: params.newPasswordToken,
    },
  );
  return response.data;
}

export async function resetPasswordWithEmailToken(params: any) {
  const response = await client.post<any>(
    `/auth/email/reset-password`,
    params.data,
  );
  return response.data;
}

export async function resetPassword(params: any) {
  const response = await client.post<any>(`/auth/reset-password`, params.data);
  return response.data;
}

export async function deleteUserAccount() {
  const response = await client.delete<any>(`/auth/leave`);
  return response.data;
}

export async function deleteMemberAccount(params: any) {
  const response = await client.delete<any>(
    `/enterprises/${params.enterpriseId}/users/${params.memberId}/leave`,
  );
  return response.data;
}

export async function checkEmailIsDuplicated(params: any) {
  const response = await client.post<any>(
    `/auth/email/check-duplicate`,
    params.data,
  );
  return response.data;
}

export async function sendInvitationToMembers(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/send-invitation-to-members`,
    params.data,
  );
  return response.data;
}

export async function getPrequalificationConfig(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/pq-config`,
  );
  return response.data;
}

export async function updatePrequalificationConfig(params: any) {
  const response = await client.patch<any>(
    `/enterprises/${params.enterpriseId}/pq-config/${params.prequalificationConfigId}`,
    params.data,
  );
  return response.data;
}

export async function createPrequalificationConfig(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/pq-config`,
    params.data,
  );
  return response.data;
}

export async function getPrequalificationList(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/prequalifications`,
  );
  return response.data;
}

export async function getPrequalificationDetail(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/prequalifications/${params.prequalificationId}`,
  );
  return response.data;
}

export async function applyVendor(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/vendors/apply`,
    params.data,
  );
  return response.data;
}

export async function getPrequalificationData(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/vendors/prequalification-data`,
  );
  return response.data;
}

export async function getPrequalificationData2(params: any) {
  const response = await client.get<any>(
    `/auth/prequalification-data?enterpriseId=${params.enterpriseId}`,
  );
  return response.data;
}

export async function requestAuthCode(params: any) {
  const response = await client.post<any>('/auth/send-code', params.data);
  return response.data;
}

export async function verifyAuthCode(params: any) {
  const response = await client.post<any>('/auth/validate-code', params.data);
  return response.data;
}

export async function sendBidResultMessage(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/projects/${params.projectId}/bid-packages/${params.bidPackageId}/bidders/${params.bidderId}/awardAlarm`,
    params.data,
  );
  return response.data;
}

export async function verifyCorporateNumber(params: any) {
  const response = await client.get<User>(
    `/auth/corporate-number/${params.corporateNumber}`,
  );
  return response.data;
}

export async function registerMember(params: any) {
  const response = await client.post<any>(`/auth/register-member`, params);
  return response.data;
}

export async function registerAdmin(params: any) {
  const response = await client.post<any>(`/auth/register-admin`, params);
  return response.data;
}

export async function getVendorList(params: VendorParams) {
  let url = `/enterprises/${params.enterpriseId}/vendors`;
  if ('filterData' in params) {
    const queryString = qs.stringify(params.filterData);
    url += '?' + queryString;
  }

  const response = await client.get<{ vendors: Vendor[] }>(url);
  return response.data;
}

export async function searchVendorList(params: VendorParams) {
  let url = `/enterprises/${params.enterpriseId}/vendors/search`;
  if ('filterData' in params) {
    const queryString = qs.stringify(params.filterData);
    url += '?' + queryString;
  }

  const response = await client.get<Vendor[]>(url);
  return response.data;
}

export async function createVendor(params: VendorParams) {
  const response = await client.post<Vendor>(
    `/enterprises/${params.enterpriseId}/vendors`,
    params.vendor,
  );
  return response.data;
}

export async function updateVendor(params: VendorParams) {
  const response = await client.patch<Vendor>(
    `/enterprises/${params.enterpriseId}/vendors/${params.vendorId}`,
    params.vendor,
  );
  return response.data;
}

export async function deleteVendor(params: VendorParams) {
  const response = await client.delete<number>(
    `/enterprises/${params.enterpriseId}/vendors/${params.vendorId}`,
  );
  return response.data;
}

export async function getVendorDetail(params: VendorParams) {
  const response = await client.get<Vendor>(
    `/enterprises/${params.enterpriseId}/vendors/${params.vendorId}`,
  );
  return response.data;
}

export async function updateVendorPQStatus(params: VendorParams) {
  const response = await client.patch<Vendor>(
    `/enterprises/${params.enterpriseId}/vendors/${params.vendorId}/qualification-status`,
    params.vendor,
  );
  return response.data;
}

export async function createVendorEvaluation(params: VendorParams) {
  const response = await client.post<VendorEvaluation>(
    `/enterprises/${params.enterpriseId}/vendors/${params.vendorId}/vendor-comments`,
    params.vendorEvaluation,
  );
  return response.data;
}

export async function deleteVendorEvaluation(params: VendorParams) {
  const response = await client.delete<number>(
    `/enterprises/${params.enterpriseId}/vendors/${params.vendorId}/vendor-comments/${params.vendorEvaluationId}`,
  );
  return response.data;
}

export async function createVendors(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/vendors/bulk-create`,
    params.data,
  );
  return response.data;
}

export async function getExpenseList(params: ExpenseParams) {
  let url = `/enterprises/${params.enterpriseId}/expenses`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/expenses`;
  if ('filterData' in params) {
    const queryString = qs.stringify(params.filterData);
    url += '?' + queryString;
  }

  const response = await client.get<{ expenses: Expense[] }>(url);
  return response.data;
}

export async function createExpense(params: ExpenseParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/expenses`;
  const response = await client.post<Expense>(url, params.expense);
  return response.data;
}

export async function updateExpense(params: ExpenseParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/expenses/${params.expenseId}`;
  const response = await client.patch<Expense>(url, params.expense);
  return response.data;
}

export async function updateExpenseWithAssetData(params: ExpenseParams) {
  let url = `/enterprises/${params.enterpriseId}/mobile/expenses/${params.expenseId}/with-asset`;
  const response = await client.patch<Expense>(url, params.expense);
  return response.data;
}

export async function excludeExpense(params: ExpenseParams) {
  let url = `/enterprises/${params.enterpriseId}/mobile/expenses/${params.expenseId}/exclude`;
  const response = await client.patch<Expense>(url, {});
  return response.data;
}

export async function deleteExpense(params: ExpenseParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/expenses/${params.expenseId}`;
  const response = await client.delete<number>(url);
  return response.data;
}

export async function getExpenseDetail(params: ExpenseParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/expenses/${params.expenseId}`;
  const response = await client.get<Expense>(url);
  return response.data;
}

export async function createExpenseComment(params: any) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/expenses/${params.expenseId}/comments`;
  const response = await client.post<Expense>(url, params.data);
  return response.data;
}

export async function changeExpensePrice(params: ExpenseParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/expenses/${params.expenseId}/change-price`;

  const response = await client.patch<Expense>(url, params.data);
  return response.data;
}

export async function bulkUpdateExpense(params: ExpenseParams) {
  const url = `/enterprises/${params.enterpriseId}/expenses/bulk-update`;
  const response = await client.patch<Expense>(url, params.data);
  return response.data;
}

export async function getExpenseCommon(enterpriseId: string) {
  const response = await client.get<any[]>(
    `/enterprises/${enterpriseId}/expenses/common`,
  );
  return response.data;
}

export async function getExpenseMyCommon(enterpriseId: string) {
  const response = await client.get<any[]>(
    `/enterprises/${enterpriseId}/expenses/my-common`,
  );
  return response.data;
}

export async function getMyAssetList(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/financial-asset/my-asset-list`,
    params.data,
  );
  return response.data;
}

export async function createFinancialAsset(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/financial-asset?type=${params.type}`,
    params.data,
  );
  return response.data;
}

export async function getFinancialAssets(
  enterpriseId: string,
  isMyAsset: boolean,
) {
  let url = `/enterprises/${enterpriseId}/financial-asset`;
  if (isMyAsset) {
    url += '/my-asset';
  } else {
    url += '/company-asset';
  }
  const response = await client.get<any[]>(url);

  return response.data;
}

export async function updateFinancialAsset(params: any) {
  const response = await client.patch<any>(
    `/enterprises/${params.enterpriseId}/financial-asset/${params.financialAssetId}`,
    params.data,
  );
  return response.data;
}

export async function deleteFinancialAssets(params: any) {
  const response = await client.delete<any>(
    `/enterprises/${params.enterpriseId}/financial-asset/${params.financialAssetId}/${params.type}`,
  );
  return response.data;
}

export async function getExpenseConfig(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/expense-config`,
  );
  return response.data;
}

export async function updateExpenseConfig(params: any) {
  const response = await client.patch<any>(
    `/enterprises/${params.enterpriseId}/expense-config`,
    params.data,
  );
  return response.data;
}

export async function getProjectConfig(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/project-config`,
  );
  return response.data;
}

export async function updateProjectConfig(params: any) {
  const response = await client.patch<any>(
    `/enterprises/${params.enterpriseId}/project-config`,
    params.data,
  );
  return response.data;
}

export async function getApprovalConfig(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/approval-config`,
  );
  return response.data;
}

export async function updateApprovalConfig(params: any) {
  const response = await client.patch<any>(
    `/enterprises/${params.enterpriseId}/approval-config`,
    params.data,
  );
  return response.data;
}

export async function syncCardApprovalList(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/financial-asset/sync-card-approval-list`,
  );
  return response.data;
}

export async function syncCardApprovalListByUser(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/financial-asset/sync-card-approval-list-by-user`,
  );
  return response.data;
}

export async function getTaxInvoiceAccounts(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/tax-invoice-accounts`,
  );
  return response.data;
}

export async function createTaxInvoiceAccounts(params: any) {
  const response = await client.post<any>(
    `/enterprises/${params.enterpriseId}/tax-invoice-accounts`,
    params.data,
  );
  return response.data;
}

export async function updateTaxInvoiceAccounts(params: any) {
  const response = await client.patch<any>(
    `/enterprises/${params.enterpriseId}/tax-invoice-accounts/${params.taxInvoiceAccountId}`,
    params.data,
  );
  return response.data;
}

export async function deleteTaxInvoiceAccounts(params: any) {
  const response = await client.delete<any>(
    `/enterprises/${params.enterpriseId}/tax-invoice-accounts/${params.taxInvoiceAccountId}`,
  );
  return response.data;
}

export async function syncTaxInvoiceList(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/tax-invoice-accounts/sync-tax-invoice-list`,
  );
  return response.data;
}

export async function getSimpleVendorList(enterpriseId: string) {
  let url = `/enterprises/${enterpriseId}/vendors/simple`;

  const response = await client.get<any[]>(url);
  return response.data;
}

export async function getInvoiceCommon(enterpriseId: string) {
  let url = `/enterprises/${enterpriseId}/invoices/common`;

  const response = await client.get<any>(url);
  return response.data;
}

export async function getInvoiceList(params: InvoiceParams) {
  let url = `/enterprises/${params.enterpriseId}/invoices`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/invoices`;
  if ('filterData' in params) {
    const queryString = qs.stringify(params.filterData);
    url += '?' + queryString;
  }

  const response = await client.get<{ invoices: Invoice[] }>(url);
  return response.data;
}

export async function createInvoice(params: InvoiceParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/invoices`;
  const response = await client.post<Invoice>(url, params.invoice);
  return response.data;
}

export async function updateInvoice(params: InvoiceParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/invoices/${params.invoiceId}`;
  const response = await client.patch<Invoice>(url, params.invoice);
  return response.data;
}

export async function updateInvoiceStatus(params: InvoiceParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/invoices/${params.invoiceId}/process-approval?approvalType=${params.approvalType}`;
  const response = await client.patch<Invoice>(url, params.invoice);
  return response.data;
}

export async function changeInvoicePrice(params: InvoiceParams) {
  let url = `/enterprises/${params.enterpriseId}/invoices/${params.invoiceId}/change-price`;
  const response = await client.patch<Invoice>(url, params.data);
  return response.data;
}

export async function deleteInvoice(params: InvoiceParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/invoices/${params.invoiceId}`;
  const response = await client.delete<number>(url);
  return response.data;
}

export async function getInvoiceDetail(params: InvoiceParams) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/invoices/${params.invoiceId}`;
  const response = await client.get<Invoice>(url);
  return response.data;
}

export async function createInvoiceComment(params: any) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/invoices/${params.invoiceId}/comments`;
  const response = await client.post<Invoice>(url, params.data);
  return response.data;
}

export async function createInvoicePayment(params: any) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/invoices/${params.invoiceId}/payments`;
  const response = await client.post<Invoice>(url, params.data);
  return response.data;
}

export async function getSubInvoiceList(params: InvoiceParams) {
  let url = `/enterprises/${params.enterpriseId}/invoices/${params.invoiceId}/sub-invoices`;
  if ('filterData' in params) {
    const queryString = qs.stringify(params.filterData);
    url += '?' + queryString;
  }

  const response = await client.get<any>(url);
  return response.data;
}

export async function createSubInvoices(params: any) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/invoices/${params.invoiceId}/sub-invoices`;
  const response = await client.post<any>(url, params.data);
  return response.data;
}

export async function excludeSubInvoices(params: any) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/invoices/${params.invoiceId}/sub-invoices/${params.subInvoiceId}`;
  const response = await client.delete<any>(url);
  return response.data;
}

export async function bulkUpdateInvoiceStatus(params: InvoiceParams) {
  let url = `/enterprises/${params.enterpriseId}/invoices/bulk-update-status`;
  const data: any = {
    idList: params.idList,
    approvalType: params.approvalType,
  };
  if ('declineReason' in params) {
    data.declineReason = params.declineReason;
  }
  const response = await client.patch<boolean>(url, data);
  return response.data;
}

export async function excludeTaxInvoices(params: InvoiceParams) {
  let url = `/enterprises/${params.enterpriseId}/tax-invoice-accounts/exclude-tax-invoices`;
  const data: any = {
    idList: params.idList,
  };
  const response = await client.patch<boolean>(url, data);
  return response.data;
}

export async function getExpenseStat2(params: any) {
  let url = `/enterprises/${params.enterpriseId}/expenses/stat2`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/expenses/stat2`;
  if ('filterData' in params) {
    const queryString = qs.stringify(params.filterData);
    url += '?' + queryString;
  }
  const response = await client.get<any>(url);
  return response.data;
}

export async function getPrimeContractStat(params: any) {
  let url = `/enterprises/${params.enterpriseId}/prime-contracts/stat`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/prime-contracts/stat`;
  // if ('filterData' in params) {
  //   const queryString = qs.stringify(params.filterData);
  //   url += '?' + queryString;
  // }
  const response = await client.get<any>(url);
  return response.data;
}

export async function getVendorConfig(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/vendor-config`,
  );
  return response.data;
}

export async function updateVendorConfig(params: any) {
  const response = await client.patch<any>(
    `/enterprises/${params.enterpriseId}/vendor-config`,
    params.data,
  );
  return response.data;
}

export async function getScheduleList(params: ScheduleParams) {
  let url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/schedules`;
  if (params.type === ScheduleListType.CHART_LIST) {
    url += '/chart-list';
  } else if (params.type === ScheduleListType.TREE_LIST) {
    url += '/tree-list';
  } else if (params.type === ScheduleListType.FORM_LIST) {
    url += '/form-list';
  }

  const response = await client.get<{ schedules: Schedule[] }>(url);
  return response.data;
}

export async function createSchedule(params: ScheduleParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/schedules`;
  const response = await client.post<Schedule>(url, params.schedule);
  return response.data;
}

export async function bulkCreateSchedule(params: ScheduleParams) {
  let url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/schedules/bulk-create`;
  if (params.scheduleId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/schedules/${params.scheduleId}/bulk-create`;

  const response = await client.post<any>(url, params.nameList);
  return response.data;
}

export async function getScheduleDetail(params: ScheduleParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/schedules/${params.scheduleId}`;
  const response = await client.get<Schedule>(url);
  return response.data;
}

export async function updateSchedule(params: ScheduleParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/schedules/${params.scheduleId}`;
  const response = await client.patch<Schedule>(url, params.schedule);
  return response.data;
}

export async function updateScheduleIndex(params: ScheduleParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/schedules/${params.scheduleId}/update-index`;
  const response = await client.patch<Schedule>(url, params.schedule);
  return response.data;
}

export async function updateScheduleDepends(params: ScheduleParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/schedules/${params.scheduleId}/update-depends`;
  const response = await client.patch<Schedule>(url, params.schedule);
  return response.data;
}

export async function deleteSchedule(params: ScheduleParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/schedules/${params.scheduleId}`;
  const response = await client.delete<number>(url);
  return response.data;
}

export async function importSchedule(params: ScheduleParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/schedules/import-schedule`;
  const response = await client.post<any>(url, params.schedule);
  return response.data;
}

export async function deleteAllSchedule(params: ScheduleParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/schedules/delete-all`;
  const response = await client.delete<number>(url);
  return response.data;
}

export async function getWorkRecordList(params: WorkRecordParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/work-records`;

  const response = await client.get<{ workRecords: WorkRecord[] }>(url);
  return response.data;
}

export async function createWorkRecord(params: WorkRecordParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/work-records`;
  const response = await client.post<WorkRecord>(url, params.workRecord);
  return response.data;
}

export async function getWorkRecordDetail(params: WorkRecordParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/work-records/${params.workRecordId}`;
  const response = await client.get<WorkRecord>(url);
  return response.data;
}

export async function updateWorkRecord(params: WorkRecordParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/work-records/${params.workRecordId}`;
  const response = await client.patch<WorkRecord>(url, params.workRecord);
  return response.data;
}

export async function deleteWorkRecord(params: WorkRecordParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/work-records/${params.workRecordId}`;
  const response = await client.delete<number>(url);
  return response.data;
}

export async function findWorkRecord(params: any) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/work-records/find?createdDate=${params.createdDate}`;

  const response = await client.get<any>(url);
  return response.data;
}

export async function getWorkRecordStat(params: WorkRecordParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/work-records/stat`;

  const response = await client.get<any>(url);
  return response.data;
}

export async function getDirectWorkerList(params: WorkRecordParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/work-records/direct-workers`;

  const response = await client.get<any>(url);
  return response.data;
}

export async function getMyExpenseList(params: ExpenseParams) {
  let url = `/enterprises/${params.enterpriseId}/mobile/expenses/not-submitted`;

  const response = await client.get<{ expenses: Expense[] }>(url);
  return response.data;
}

export async function getMemberInvitationUrl() {
  const url = `/auth/get-member-invitation-url`;
  const response = await client.post<any>(url, {});
  return response.data;
}

export async function getMemberInvitationInfo(params: any) {
  const url = `/auth/member-invitation-info?token=${params.token}&v=${params.v}`;

  const response = await client.get<any>(url);
  return response.data;
}

export async function getToDoList(params: any) {
  const url = `/enterprises/${params.enterpriseId}/todos`;
  const response = await client.get<WorkRecord>(url);
  return response.data;
}

export async function getTaskList(params: TaskParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/tasks`;

  const response = await client.get<{ tasks: Task[] }>(url);
  return response.data;
}

export async function createTask(params: TaskParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/tasks`;
  const response = await client.post<Task>(url, params.task);
  return response.data;
}

export async function getTaskDetail(params: TaskParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/tasks/${params.taskId}`;
  const response = await client.get<Task>(url);
  return response.data;
}

export async function updateTask(params: TaskParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/tasks/${params.taskId}`;
  const response = await client.patch<Task>(url, params.task);
  return response.data;
}

export async function deleteTask(params: TaskParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/tasks/${params.taskId}`;
  const response = await client.delete<number>(url);
  return response.data;
}

export async function createTaskComment(params: any) {
  let url = `/enterprises/${params.enterpriseId}`;
  if (params.projectId)
    url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}`;
  url += `/tasks/${params.taskId}/comments`;
  const response = await client.post<Task>(url, params.data);
  return response.data;
}

export async function changeStatusTask(params: TaskParams) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/tasks/${params.taskId}/change-status`;
  const response = await client.patch<Task>(url, params.task);
  return response.data;
}

// export async function getEnterpriseMemberList(params: VendorParams) {
//   let url = `/enterprises/${params.enterpriseId}/vendors`;
//   if ('filterData' in params) {
//     const queryString = qs.stringify(params.filterData);
//     url += '?' + queryString;
//   }

//   const response = await client.get<{ vendors: Vendor[] }>(url);
//   return response.data;
// }

export async function importTaxInvoiceList(params: any) {
  let url = `/enterprises/${params.enterpriseId}/tax-invoice-accounts/import-tax-invoice-list`;
  if ('filterData' in params) {
    const queryString = qs.stringify(params.filterData);
    url += '?' + queryString;
  }

  const response = await client.get<any>(url);
  return response.data;
}

export async function getTaxInvoiceList(params: any) {
  let url = `/enterprises/${params.enterpriseId}/tax-invoice-accounts/tax-invoices`;
  if ('filterData' in params) {
    const queryString = qs.stringify(params.filterData);
    url += '?' + queryString;
  }

  const response = await client.get<{ taxInvoices: TaxInvoice[] }>(url);
  return response.data;
}

export async function createTaxInvoice(params: any) {
  let url = `/enterprises/${params.enterpriseId}/tax-invoice-accounts/tax-invoices`;

  const response = await client.post<TaxInvoice>(url, params.data);
  return response.data;
}

export async function updateTaxInvoice(params: any) {
  let url = `/enterprises/${params.enterpriseId}/tax-invoice-accounts/tax-invoices/${params.taxInvoiceId}`;

  const response = await client.patch<TaxInvoice>(url, params.data);
  return response.data;
}

export async function deleteTaxInvoice(params: any) {
  let url = `/enterprises/${params.enterpriseId}/tax-invoice-accounts/tax-invoices/${params.taxInvoiceId}`;

  const response = await client.delete<any>(url, params.data);
  return response.data;
}

export async function searchInvoiceForTaxInvoice(params: InvoiceParams) {
  let url = `/enterprises/${params.enterpriseId}/invoices/search-invoice`;
  if ('filterData' in params) {
    const queryString = qs.stringify(params.filterData);
    url += '?' + queryString;
  }

  const response = await client.get<any>(url);
  return response.data;
}

export async function mappingInvoiceToTaxInvoice(params: any) {
  let url = `/enterprises/${params.enterpriseId}/tax-invoice-accounts/tax-invoices/${params.taxInvoiceId}/mapping-invoice`;

  const response = await client.patch<TaxInvoice>(url, params.data);
  return response.data;
}

export async function mappingTaxInvoiceToInvoice(params: any) {
  let url = `/enterprises/${params.enterpriseId}/tax-invoice-accounts/invoices/${params.invoiceId}/mapping-tax-invoice`;

  const response = await client.patch<TaxInvoice>(url, {
    idList: params.idList,
  });
  return response.data;
}

export async function searchTaxInvoiceForInvoice(params: InvoiceParams) {
  let url = `/enterprises/${params.enterpriseId}/tax-invoice-accounts/tax-invoices`;
  if ('filterData' in params) {
    const queryString = qs.stringify(params.filterData);
    url += '?' + queryString;
  }

  const response = await client.get<any>(url);
  return response.data;
}

export async function getProjectRevenueList(params: any) {
  let url = `/enterprises/${params.enterpriseId}/projects/revenues`;
  let queryString = '';
  if ('filter' in params) {
    queryString = qs.stringify(params.filter);
  }

  if (queryString) url += '?' + queryString;

  const response = await client.get<any>(url);
  return response.data;
}

export async function getProjectRevenue(params: any) {
  const response = await client.get<any>(
    `/enterprises/${params.enterpriseId}/projects/revenues/${params.projectId}`,
  );
  return response.data;
}

export async function createIncome(params: any) {
  let url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/incomes`;

  const response = await client.post<any>(url, params.data);
  return response.data;
}

export async function updateIncome(params: any) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/incomes/${params.incomeId}`;
  const response = await client.patch<any>(url, params.data);
  return response.data;
}

export async function deleteIncome(params: any) {
  const url = `/enterprises/${params.enterpriseId}/projects/${params.projectId}/incomes/${params.incomeId}`;
  const response = await client.delete<any>(url);
  return response.data;
}

export async function getPrimeContractList(params: any) {
  const response = await client.get<any[]>(
    `/enterprises/${params.enterpriseId}/common/prime-contracts`,
  );
  return response.data;
}

export async function getPrimeContractGongjongList(params: any) {
  const response = await client.get<any[]>(
    `/enterprises/${params.enterpriseId}/common/prime-contracts/${params.primeContractId}/gongjong-list`,
  );
  return response.data;
}
